import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useState, lazy, Suspense, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SITE_NAME } from "@/lib/gold-data";
// Non-critical UI: loaded after the browser is idle so it never competes with
// LCP/FCP work on the main thread.
const ClipboardHijacker = lazy(() =>
  import("@/components/ClipboardHijacker").then((m) => ({ default: m.ClipboardHijacker })),
);
const IndexNowObserver = lazy(() =>
  import("@/components/IndexNowObserver").then((m) => ({ default: m.IndexNowObserver })),
);
const OrganicPushPrompt = lazy(() =>
  import("@/components/OrganicPushPrompt").then((m) => ({ default: m.OrganicPushPrompt })),
);

function useIdleReady(delay = 2000): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const idle =
      (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
        .requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1));
    const t = setTimeout(() => idle(() => setReady(true), { timeout: 1000 }), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return ready;
}
function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { name: "author", content: "DrHint" },
      { property: "og:site_name", content: "DrHint" },
      { property: "og:type", content: "website" },
      { name: "google-site-verification", content: "CDMjdPPbGZfEKXvRmNkIaqYiYla0sEXoBg2mSCrgeJ4" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico?v=4", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png?v=4" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png?v=4" },
      { rel: "shortcut icon", type: "image/x-icon", href: "/favicon.ico?v=4" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png?v=4" },
      { rel: "dns-prefetch", href: "https://pub-49eb185b9bd24cefb7b07e3eb5c79572.r2.dev" },
      { rel: "dns-prefetch", href: "https://securepubads.g.doubleclick.net" },
      { rel: "dns-prefetch", href: "https://googleads.g.doubleclick.net" },
      { rel: "dns-prefetch", href: "https://tpc.googlesyndication.com" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://drhint.com/#website",
          url: "https://drhint.com/",
          name: "DrHint",
          alternateName: ["Dr Hint", "DrHint Live Rates"],
          description: "Live 24K, 22K and 21K gold prices for Pakistan and India, recalculated every 10 minutes from international spot and live USD exchange rate.",
          publisher: { "@type": "Organization", name: "DrHint", url: "https://drhint.com/" },
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate: "https://drhint.com/search?q={search_term_string}",
            },
            "query-input": "required name=search_term_string",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "DrHint primary navigation",
          itemListElement: [
            { "@type": "SiteNavigationElement", position: 1, name: "Live Gold Rate Pakistan", url: "https://drhint.com/pakistan" },
            { "@type": "SiteNavigationElement", position: 2, name: "Live Gold Rate India", url: "https://drhint.com/india" },
            { "@type": "SiteNavigationElement", position: 3, name: "Gold Tools Suite", url: "https://drhint.com/tools" },
            { "@type": "SiteNavigationElement", position: 4, name: "Free Gold Rate API", url: "https://drhint.com/developer/gold-rate-api" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://drhint.com/#organization",
          name: "DrHint",
          url: "https://drhint.com/",
          logo: "https://drhint.com/icon-512.png",
          sameAs: [
            "https://drhint.com/about",
          ],
        }),
      },
      {
        // GA4 bootstrap only. Google Ad Manager is initialized by DisplayAd
        // after each slot div exists, so GPT never enables before slots mount.
        children:
          "(function(){window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;var idle=window.requestIdleCallback||function(cb){return setTimeout(cb,1);};function loadGA(){if(window.__dh_ga)return;window.__dh_ga=true;var g=document.createElement('script');g.async=true;g.src='https://www.googletagmanager.com/gtag/js?id=G-T04DVWJFNJ';document.head.appendChild(g);gtag('js',new Date());gtag('config','G-T04DVWJFNJ',{send_page_view:true});}['pointerdown','keydown','scroll'].forEach(function(e){addEventListener(e,loadGA,{once:true,passive:true});});setTimeout(function(){idle(loadGA,{timeout:2000});},5000);})();",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const idleReady = useIdleReady();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      {idleReady ? (
        <Suspense fallback={null}>
          <OrganicPushPrompt />
          {/* CookieConsent temporarily disabled — Europe traffic negligible right now. */}
          <ClipboardHijacker />
          {/* Floating WhatsApp FAB removed — inline share + heart + auto-posts cover this. */}
          <IndexNowObserver />
        </Suspense>
      ) : null}
    </QueryClientProvider>
  );
}

